

import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import './AddBusiness.css';
import paymentIconOn from '../../assets/images/New_Business_Add_Pay_Chack_Box_On.png'
import paymentIconOff from '../../assets/images/New_Business_Add_Pay_Chack_Box_Off.png'
import { Business, BusinessType, PaymentOption } from '../../Items/Business';
import { addBusiness, getBusinessTypes, getPaymentOptions, toggleIsShow } from '../../Services/BusinessService';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

interface AddBusinessProps {
  onSave: (business: Business, isEdit: boolean) => void;
  initialBusiness?: Business | null;
}

export interface AddBusinessRef {
  saveBusiness: () => void;
}

const AddBusiness = forwardRef<AddBusinessRef, AddBusinessProps>(({ onSave, initialBusiness }, ref) => {
  const [logo, setLogo] = useState<string | null>(null);
  const [business, setBusiness] = useState<Business>(initialBusiness || {
    id: 0,
    name: '',
    nameOfOwner: '',
    email: '',
    phone: '',
    phoneOfOwner: '',
    address: '',
    uploadLogo: '',
    businessTypeId: 0,
    businessType: null,
    isShow: initialBusiness ? (initialBusiness as Business).isShow : true,
    paymentOptionId: 0,
    notes: ''
  });

  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchPaymentOptions = async () => {
      try {
        const options = await getPaymentOptions();
        setPaymentOptions(options);
        
        const types = await getBusinessTypes();
        setBusinessTypes(types);
      } catch (error) {
        console.error("Error fetching payment options", error);
      }
    };
    fetchPaymentOptions();
  }, []);

  useEffect(() => {
    if (initialBusiness) {
      setBusiness(initialBusiness);
      if (typeof initialBusiness.uploadLogo === 'string') {
        setLogo(initialBusiness.uploadLogo);
      }
    }
  }, [initialBusiness]);

  const handleChange = (field: keyof Business, value: any) => {
    setBusiness(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleChange('uploadLogo', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogo(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const isFormValid = () => {
    return (
      business.name &&
      business.nameOfOwner &&
      business.email &&
      business.phone &&
      business.phoneOfOwner &&
      business.address &&
      business.paymentOptionId > 0
    );
  };

  const handleSave = async () => {
    if (!isFormValid()) return;

    try {
      let dataToSend: Partial<Business> = {};

      if (initialBusiness) {
        (Object.keys(business) as (keyof Business)[]).forEach((key) => {
          const value = business[key];
          if (value !== undefined && value !== null && value !== initialBusiness[key]) {
            (dataToSend as any)[key] = value;
          }
        });
        dataToSend.id = business.id;
        dataToSend = { ...initialBusiness, ...dataToSend };
      } else {
        dataToSend = business;
      }

      await onSave(dataToSend as Business, !!initialBusiness);
      alert(initialBusiness ? "העסק עודכן בהצלחה!" : "העסק נוסף בהצלחה!");
      navigate("/business");
    } catch (error) {
      console.error("שגיאה בשמירת העסק:", error);
      alert("שגיאה בשמירת העסק");
    }
  };

  // Expose save function to parent via ref
  useImperativeHandle(ref, () => ({
    saveBusiness: handleSave
  }));

  return (
    <div className="add-business-container-ab">
      <form className="add-business-form-ab" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="form-grid-ab">
          <input type="text" placeholder="שם העסק" value={business.name} onChange={e => handleChange('name', e.target.value)} />
          <select value={business.businessTypeId} onChange={e => handleChange('businessTypeId', +e.target.value)}>
            <option value="">סוג העסק</option>
            {businessTypes.map(bt => (
              <option key={bt.id} value={bt.id}>{bt.name}</option>
            ))}
          </select>
          <input type="text" placeholder="שם בעל העסק" value={business.nameOfOwner} onChange={e => handleChange('nameOfOwner', e.target.value)} />
          <input type="text" placeholder="טלפון בעל העסק" value={business.phoneOfOwner} onChange={e => handleChange('phoneOfOwner', e.target.value)} />
          <input type="text" placeholder="טלפון העסק" value={business.phone} onChange={e => handleChange('phone', e.target.value)} />
          <input type="email" placeholder="אימייל" value={business.email} onChange={e => handleChange('email', e.target.value)} />
          <input type="text" placeholder="כתובת" value={business.address} className="address-field" onChange={e => handleChange('address', e.target.value)} />
          <div className="payment-type-ab grid-payment">
  <span className="payment-label">סוג תשלום מאושר:</span>
  {paymentOptions.map(option => (
    <label key={option.id} className="custom-radio">
      <input
        type="radio"
        name="payment"
        value={option.id}
        checked={business.paymentOptionId === option.id}
        onChange={() => handleChange('paymentOptionId', option.id)}
      />
      <img
        src={business.paymentOptionId === option.id ? paymentIconOn : paymentIconOff}
        alt="radio option"
        className="radio-image"
      />
      {option.optionName}
    </label>
  ))}
</div>
        </div>
        <div className="bottom-section-ab">
          <div className="notes-section">
            <label>הערות</label>
            <textarea className='comments' value={business.notes} onChange={e => handleChange('notes', e.target.value)}></textarea>
          </div>
          <div className="logo-upload-ab">
            <label>העלאת לוגו של העסק</label>
            <div className="logo-box" onClick={handleLogoClick}>
              {logo ? <img src={logo} alt="לוגו" /> : <span>340X340 pixel</span>}
            </div>
            <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleLogoChange} />
          </div>
        </div>
      </form>
    </div>
  );
});

export default AddBusiness;
